import { useEffect, useRef, useState } from "react";
import { gamesApi } from "@/lib/api";
import ArcadeShell from "@/components/games/ArcadeShell";

const W = 360;
const H = 480;
const GROUND = H - 60;

const SPEED_CFG = {
  slow: { vel: 0.75, seconds: 50 },
  normal: { vel: 1.0, seconds: 40 },
  fast: { vel: 1.35, seconds: 32 },
};

// Player-chosen difficulty tiers — faster/denser flocks pay more per hit (separate leaderboards).
const DIFFICULTIES = {
  easy: { label: "Easy", blurb: "Slower ducks, extra shots", velMul: 0.75, baseDucks: 1, shots: 12, secondsMul: 1.15, scoreMul: 1 },
  normal: { label: "Normal", blurb: "The classic hunt", velMul: 1.0, baseDucks: 2, shots: 10, secondsMul: 1, scoreMul: 1.5 },
  hard: { label: "Hard", blurb: "Fast, dense flocks — top points", velMul: 1.4, baseDucks: 3, shots: 9, secondsMul: 0.9, scoreMul: 2 },
};

// Rotating scenes (jungle, open land, mountains, sky).
const SCENES = [
  { name: "Jungle", sky: ["#8FD17A", "#C9E9A7"], ground: "#2F5E34", strip: "#3C7A3F" },
  { name: "Open land", sky: ["#BFE3F2", "#EAF6EC"], ground: "#B98B46", strip: "#A9792F" },
  { name: "Mountains", sky: ["#CDD8EF", "#EEF3FB"], ground: "#6B7A8F", strip: "#55616F" },
  { name: "Sky", sky: ["#7FB3F2", "#D6ECFF"], ground: "#4C6B9A", strip: "#5E7FB0" },
];

function beep(type) {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = beep._ctx || (beep._ctx = new AC());
    const now = ctx.currentTime;
    if (type === "shoot") {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "square"; o.frequency.setValueAtTime(180, now); o.frequency.exponentialRampToValueAtTime(40, now + 0.12);
      g.gain.setValueAtTime(0.25, now); g.gain.exponentialRampToValueAtTime(0.001, now + 0.14);
      o.connect(g).connect(ctx.destination); o.start(now); o.stop(now + 0.15);
    } else if (type === "hit") {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sawtooth"; o.frequency.setValueAtTime(500, now); o.frequency.exponentialRampToValueAtTime(120, now + 0.18);
      g.gain.setValueAtTime(0.2, now); g.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
      o.connect(g).connect(ctx.destination); o.start(now); o.stop(now + 0.2);
    } else if (type === "pickup") {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "triangle"; o.frequency.setValueAtTime(440, now); o.frequency.linearRampToValueAtTime(880, now + 0.12);
      g.gain.setValueAtTime(0.18, now); g.gain.exponentialRampToValueAtTime(0.001, now + 0.16);
      o.connect(g).connect(ctx.destination); o.start(now); o.stop(now + 0.16);
    }
  } catch { /* audio not available */ }
}

function makeDuck(wave, speedMul) {
  const fromLeft = Math.random() < 0.5;
  const base = (1.1 + wave * 0.18 + Math.random() * 0.5) * speedMul;
  return {
    x: fromLeft ? -20 : W + 20,
    y: 60 + Math.random() * (GROUND - 180),
    vx: (fromLeft ? 1 : -1) * base,
    vy: (Math.random() - 0.5) * base * 0.9,
    r: 20, alive: true, falling: false, landed: false, flap: Math.random() * Math.PI,
  };
}

function DuckBoard({ onGameOver, speed }) {
  const [difficulty, setDifficulty] = useState(null);
  if (!difficulty) {
    return (
      <div className="flex w-full max-w-[360px] flex-col items-center gap-4" data-testid="duck-difficulty-select">
        <p className="font-display text-lg font-bold">Choose your challenge</p>
        <div className="grid w-full gap-2.5">
          {Object.entries(DIFFICULTIES).map(([key, d]) => (
            <button
              key={key}
              onClick={() => setDifficulty(key)}
              data-testid={`duck-difficulty-${key}`}
              className="flex items-center justify-between rounded-2xl border border-border bg-card p-4 text-left transition hover:border-primary/50"
            >
              <div>
                <p className="font-display text-base font-bold">{d.label}</p>
                <p className="text-xs text-muted-foreground">{d.blurb}</p>
              </div>
              <span className="rounded-full bg-[hsl(var(--brand-tertiary))] px-3 py-1 text-xs font-bold text-primary">×{d.scoreMul} pts</span>
            </button>
          ))}
        </div>
      </div>
    );
  }
  return <DuckGame onGameOver={onGameOver} speed={speed} difficulty={difficulty} />;
}

function DuckGame({ onGameOver, speed, difficulty }) {
  const canvasRef = useRef(null);
  const diff = DIFFICULTIES[difficulty] || DIFFICULTIES.normal;
  const cfg = SPEED_CFG[speed] || SPEED_CFG.normal;
  const vel = cfg.vel * diff.velMul;
  const seconds = cfg.seconds * diff.secondsMul;
  const [hud, setHud] = useState({ score: 0, shots: diff.shots, time: 0, wave: 1, combo: 0 });
  const [summary, setSummary] = useState(null);

  const g = useRef({
    ducks: [], fallen: [], hunter: null, splashes: [],
    score: 0, shots: diff.shots, wave: 1, combo: 0, bestCombo: 0, hits: 0, fired: 0,
    endAt: 0, over: false,
  });

  const spawnWave = (st, wave) => { st.ducks = Array.from({ length: diff.baseDucks + wave }, () => makeDuck(wave, vel)); };

  const endGame = (st) => {
    if (st.over) return;
    st.over = true;
    setSummary({ score: st.score, hits: st.hits, fired: st.fired, accuracy: st.fired ? Math.round((st.hits / st.fired) * 100) : 0, wave: st.wave, bestCombo: st.bestCombo });
  };

  const shoot = (cx, cy) => {
    const st = g.current;
    if (st.over || st.shots <= 0) return;
    st.shots -= 1; st.fired += 1;
    beep("shoot");
    st.splashes.push({ x: cx, y: cy, life: 12, hit: false });
    let target = null, bestD = Infinity;
    for (const d of st.ducks) {
      if (!d.alive) continue;
      const dist = Math.hypot(d.x - cx, d.y - cy);
      if (dist <= d.r + 10 && dist < bestD) { bestD = dist; target = d; }
    }
    if (target) {
      target.alive = false; target.falling = true; target.vx = 0; target.vy = 2.5;
      st.hits += 1; st.combo += 1; st.bestCombo = Math.max(st.bestCombo, st.combo);
      st.score += Math.round(10 * Math.min(5, st.combo) * diff.scoreMul);
      st.splashes[st.splashes.length - 1].hit = true;
      beep("hit");
      if (st.ducks.every((d) => !d.alive)) {
        st.wave += 1; st.score += Math.round(25 * diff.scoreMul); st.shots += 4; spawnWave(st, st.wave);
      }
    } else {
      st.combo = 0;
    }
    if (st.shots <= 0 && st.ducks.some((d) => d.alive)) endGame(st);
  };

  const onPointer = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    shoot(((e.clientX - rect.left) / rect.width) * W, ((e.clientY - rect.top) / rect.height) * H);
  };

  useEffect(() => {
    const st = g.current;
    st.endAt = performance.now() + seconds * 1000;
    spawnWave(st, 1);
    const ctx = canvasRef.current.getContext("2d");
    let raf;

    const drawDuck = (d, dead) => {
      const dir = d.vx >= 0 ? 1 : -1;
      ctx.save(); ctx.translate(d.x, d.y); ctx.scale(dir, 1);
      if (dead) ctx.rotate(0.5);
      ctx.fillStyle = "#B5651D"; ctx.beginPath(); ctx.ellipse(0, 0, 20, 14, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#F4E2C8"; ctx.beginPath(); ctx.ellipse(-2, 5, 12, 7, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#1F7A3D"; ctx.beginPath(); ctx.arc(14, -8, 9, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#E8A23D"; ctx.beginPath(); ctx.moveTo(22, -9); ctx.lineTo(32, -6); ctx.lineTo(22, -3); ctx.closePath(); ctx.fill();
      if (!dead) { const wing = Math.sin(d.flap) * 8; ctx.fillStyle = "#8A4C14"; ctx.beginPath(); ctx.ellipse(-4, -2 - wing / 2, 10, 6, -0.5, 0, Math.PI * 2); ctx.fill(); }
      ctx.restore();
    };

    const drawHunter = (hx) => {
      ctx.save(); ctx.translate(hx, GROUND);
      ctx.fillStyle = "#3A2E22"; ctx.fillRect(-6, -42, 12, 24); // body
      ctx.fillStyle = "#E7C9A0"; ctx.beginPath(); ctx.arc(0, -48, 7, 0, Math.PI * 2); ctx.fill(); // head
      ctx.fillStyle = "#6B4A2A"; ctx.fillRect(-9, -54, 18, 5); // hat brim
      ctx.fillRect(-5, -60, 10, 7); // hat top
      ctx.strokeStyle = "#3A2E22"; ctx.lineWidth = 4;
      const sw = Math.sin(performance.now() / 120) * 5;
      ctx.beginPath(); ctx.moveTo(-3, -18); ctx.lineTo(-3 - sw, 0); ctx.moveTo(3, -18); ctx.lineTo(3 + sw, 0); ctx.stroke(); // legs
      ctx.restore();
    };

    const draw = (now) => {
      const timeLeft = Math.max(0, (st.endAt - now) / 1000);
      if (!st.over && timeLeft <= 0) endGame(st);
      const scene = SCENES[(st.wave - 1) % SCENES.length];

      for (const d of st.ducks) {
        if (d.falling) {
          d.y += d.vy; d.vy += 0.25;
          if (d.y >= GROUND - 10) { d.falling = false; d.landed = true; st.fallen.push({ x: Math.max(20, Math.min(W - 20, d.x)), collected: false }); d.remove = true; }
        } else if (d.alive) {
          d.x += d.vx; d.y += d.vy; d.flap += 0.3;
          if (d.y < 50 || d.y > GROUND - 120) d.vy *= -1;
          if (d.x < -40 || d.x > W + 40) Object.assign(d, makeDuck(st.wave, vel));
        }
      }
      st.ducks = st.ducks.filter((d) => !d.remove);

      // hunter logic — walk to the nearest fallen duck and collect it
      const pending = st.fallen.find((f) => !f.collected);
      if (pending && !st.hunter) st.hunter = { x: pending.x < W / 2 ? -20 : W + 20, target: pending.x, ref: pending, phase: "in" };
      if (st.hunter) {
        const h = st.hunter;
        if (h.phase === "in") {
          h.x += h.x < h.target ? 2.2 : -2.2;
          if (Math.abs(h.x - h.target) < 4) { h.phase = "pick"; h.wait = 18; beep("pickup"); h.ref.collected = true; }
        } else if (h.phase === "pick") {
          h.wait -= 1; if (h.wait <= 0) h.phase = "out";
        } else {
          h.x += h.x < W / 2 ? -2.2 : 2.2;
          if (h.x < -30 || h.x > W + 30) { st.fallen = st.fallen.filter((f) => !f.collected); st.hunter = null; }
        }
      }

      // background
      const sky = ctx.createLinearGradient(0, 0, 0, GROUND);
      sky.addColorStop(0, scene.sky[0]); sky.addColorStop(1, scene.sky[1]);
      ctx.fillStyle = sky; ctx.fillRect(0, 0, W, GROUND);
      ctx.fillStyle = scene.ground; ctx.fillRect(0, GROUND, W, H - GROUND);
      ctx.fillStyle = scene.strip; ctx.fillRect(0, GROUND, W, 10);

      for (const f of st.fallen) if (!f.collected) { ctx.fillStyle = "#8A4C14"; ctx.beginPath(); ctx.ellipse(f.x, GROUND - 6, 14, 8, 0, 0, Math.PI * 2); ctx.fill(); }
      for (const d of st.ducks) drawDuck(d, d.falling);
      if (st.hunter) drawHunter(st.hunter.x);

      st.splashes = st.splashes.filter((s) => s.life > 0);
      for (const s of st.splashes) { s.life -= 1; ctx.strokeStyle = s.hit ? "#1F7A3D" : "#C0392B"; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(s.x, s.y, (14 - s.life) * 1.6, 0, Math.PI * 2); ctx.stroke(); }

      ctx.fillStyle = "rgba(0,0,0,0.45)"; ctx.font = "bold 11px sans-serif"; ctx.fillText(scene.name, 10, 18);

      setHud({ score: st.score, shots: st.shots, time: Math.ceil(timeLeft), wave: st.wave, combo: st.combo });
      if (!st.over) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [seconds, vel]);

  const comboMult = Math.min(5, hud.combo);

  return (
    <div className="flex w-full flex-col items-center gap-3">
      <div className="flex w-full max-w-[360px] items-center justify-between text-sm font-semibold">
        <span data-testid="duck-score">🎯 {hud.score}</span>
        <span className={comboMult > 1 ? "text-primary" : "text-muted-foreground"}>Combo ×{Math.max(1, comboMult)}</span>
        <span>Wave {hud.wave}</span>
      </div>
      <div className="flex w-full max-w-[360px] items-center justify-between text-xs text-muted-foreground">
        <span data-testid="duck-shots">🔫 Shots: {hud.shots}</span>
        <span data-testid="duck-time">⏱ {hud.time}s</span>
      </div>
      <div className="relative w-full max-w-[360px]">
        <canvas ref={canvasRef} width={W} height={H} onPointerDown={onPointer} data-testid="duck-canvas" className="w-full cursor-crosshair touch-none rounded-2xl border border-border shadow-sm" style={{ aspectRatio: `${W} / ${H}` }} />
        {summary ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-2xl bg-background/92 p-6 text-center backdrop-blur-sm" data-testid="duck-summary">
            <p className="font-display text-xl font-bold">Round complete! 🦆</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <span className="text-muted-foreground">Score</span><span className="text-right font-bold">{summary.score}</span>
              <span className="text-muted-foreground">Ducks hit</span><span className="text-right font-bold">{summary.hits}</span>
              <span className="text-muted-foreground">Accuracy</span><span className="text-right font-bold">{summary.accuracy}%</span>
              <span className="text-muted-foreground">Best combo</span><span className="text-right font-bold">×{Math.max(1, summary.bestCombo)}</span>
              <span className="text-muted-foreground">Waves</span><span className="text-right font-bold">{summary.wave}</span>
            </div>
            <button onClick={() => onGameOver(summary.score, difficulty)} data-testid="duck-collect" className="mt-1 rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground">Collect points</button>
          </div>
        ) : null}
      </div>
      <p className="text-xs text-muted-foreground">Tap the ducks to shoot. The hunter fetches each one you drop!</p>
    </div>
  );
}

export default function DuckHunt() {
  return (
    <ArcadeShell title="Duck Hunt" game="duck" stateKey="duckState" stateFn={gamesApi.duckState} unlockFn={gamesApi.duckUnlock} scoreFn={gamesApi.duckScore}>
      {({ onGameOver, speed }) => <DuckBoard onGameOver={onGameOver} speed={speed} />}
    </ArcadeShell>
  );
}
